import { isAnyUnitPermission, type SystemRolePermissionId } from '@clockwise/shared';
import { createContext, createElement, type ReactNode, useContext } from 'react';
import { useMe } from './me';

/**
 * The Business Unit of the record a screen is about (a customer, a role, an
 * employee's primary employer), set by {@link RecordUnitProvider}.
 */
const RecordUnitContext = createContext<string | null>(null);

/**
 * Inside, the permission checks that the server judges in a record's unit —
 * the `ANY_UNIT_PERMISSIONS` (customers and their contacts, team, SOWs,
 * budgets and expenses; employees; roles) — answer for THIS record's unit:
 * the caller's role there decides, as it does on the server
 * (docs/business-units.md → "Route gates"). Every other permission is checked
 * on the caller's own role, as the server's gates do. Wrap a record's page in
 * it once its unit is known; `unitId` null/undefined changes nothing.
 */
export function RecordUnitProvider({
  unitId,
  children,
}: {
  unitId: string | null | undefined;
  children: ReactNode;
}) {
  return createElement(RecordUnitContext.Provider, { value: unitId ?? null }, children);
}

/** The predicate behind {@link useHasPermission}: for the record in
 *  context, an any-unit permission is held when the caller's role in its
 *  unit holds it; anything else, and outside a record, by presence. */
function useHolds(): (id: SystemRolePermissionId) => boolean {
  const { permissions, businessUnits } = useMe();
  const recordUnit = useContext(RecordUnitContext);
  // `/me` carries each unit's permissions; before it has (or for a caller in
  // no unit), presence decides.
  const here = recordUnit ? businessUnits.find((u) => u.id === recordUnit) : undefined;
  const perUnitLoaded = businessUnits.some((u) => u.permissions != null);
  return (id) => {
    if (!recordUnit || !perUnitLoaded || !isAnyUnitPermission(id)) return permissions.has(id);
    return (here?.permissions ?? []).some((e) => e.permission === id);
  };
}

/**
 * Returns true iff the calling user has the given permission id.
 *
 * Reads from the cached `/api/users/me` payload — no extra request. While the
 * payload is loading the hook returns `false`; callers that want to suppress
 * a flash of "no access" UI should check `useMe().loading` separately.
 *
 * Presence-only: scope ids declared in the registry (`all`, `customer_team`,
 * `managed`, `assigned`) are stored on the role but not enforced here. Inside
 * a {@link RecordUnitProvider}, a permission judged per Business Unit answers
 * for that record's unit.
 */
export function useHasPermission(id: SystemRolePermissionId): boolean {
  return useHolds()(id);
}

/**
 * Reads multiple permissions at once. Cheaper than calling `useHasPermission`
 * N times when a screen gates many UI elements.
 */
export function useHasPermissions<TIds extends readonly SystemRolePermissionId[]>(
  ids: TIds,
): Record<TIds[number], boolean> {
  const holds = useHolds();
  const out: Partial<Record<TIds[number], boolean>> = {};
  for (const id of ids) {
    (out as Record<string, boolean>)[id] = holds(id);
  }
  return out as Record<TIds[number], boolean>;
}

/**
 * Whether the caller holds `id` at scope All (or scopeless) on their own role
 * AND in every Business Unit they work in — what a tenant-wide import takes on
 * the server (`isUnrestrictedEverywhere`). Presence alone would offer an
 * import that a narrower scope then refuses.
 */
export function useHoldsUnrestricted(id: SystemRolePermissionId): boolean {
  const { permissionEntries, businessUnits } = useMe();
  const atAll = (entries: readonly { permission: string; scope?: string | null }[]) =>
    entries.some((e) => e.permission === id && (e.scope == null || e.scope === 'all'));
  if (!atAll(permissionEntries)) return false;
  // Before `/me` carries each unit's role (or for a caller in one unit), the
  // own role decides — as the server does with one unit.
  if (!businessUnits.some((u) => u.permissions != null)) return true;
  return businessUnits.every((u) => atAll(u.permissions ?? []));
}
